import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, BASE_URL, PREFIX } from './Config';
import axios from "axios";
import Swal from 'sweetalert2';
import { encryptPayload, resolveEncryptedResponse } from '../utils/customerCrypto';
import { prefetchCompanySettings, clearCompanySettingsCache } from '../utils/companySettingsCache';
import {
  clearUserRolesCache,
  markRolesSessionHydrated,
  prefetchUserRolesForPermission,
  prefetchUserRolesWithPermissions,
  fetchUserRolesWithPermissionsCached,
} from '../utils/userRolesCache';


function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [options, setOptions] = useState([]);
    const [selectedTill, setSelectedTill] = useState('0');
    const [selectedTillName, setSelectedTillName] = useState('');
    const [tillInfo, setTillInfo] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const modalRef = useRef(null);
    const accessToken = localStorage.getItem('accessToken');

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    const getResponseMessage = async (response, fallbackMessage) => {
        try {
            const data = await response.json();
            return data?.message || fallbackMessage;
        } catch {
            return fallbackMessage;
        }
    };

    const postAuthForm = async (endpoint, fields) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value ?? '');
        });

        const response = await fetch(`${API_URL}/${endpoint}`, {
            method: 'POST',
            body: formData,
        });

        let data = {};
        try {
            data = await response.json();
        } catch {
            data = {};
        }

        if (!response.ok || data?.success === false) {
            throw new Error(data?.message || 'Request failed');
        }

        return data;
    };

    const handleForgotPassword = async () => {
        const { value: forgotUsername } = await Swal.fire({
            title: 'Forgot Password',
            input: 'text',
            inputLabel: 'Username',
            inputPlaceholder: 'Enter your username',
            inputValue: username || '',
            showCancelButton: true,
            confirmButtonText: 'Send OTP',
            confirmButtonColor: '#57d6d4ff',
            inputValidator: (value) => {
                if (!value || !value.trim()) {
                    return 'Please enter username';
                }
                return null;
            },
        });

        if (!forgotUsername) return;

        const normalizedUsername = forgotUsername.trim();

        try {
            await postAuthForm('ws-forgot-password', {
                username: normalizedUsername,
            });

            const { value: otp } = await Swal.fire({
                title: 'Verify OTP',
                input: 'text',
                inputLabel: 'OTP',
                inputPlaceholder: 'Enter OTP',
                showCancelButton: true,
                confirmButtonText: 'Verify OTP',
                confirmButtonColor: '#57d6d4ff',
                inputValidator: (value) => {
                    if (!value || !value.trim()) {
                        return 'Please enter OTP';
                    }
                    return null;
                },
            });

            if (!otp) return;

            const normalizedOtp = otp.trim();

            await postAuthForm('ws-verify-otp', {
                username: normalizedUsername,
                otp: normalizedOtp,
            });

            const { value: resetValues } = await Swal.fire({
                title: 'Reset Password',
                html: `
                    <input type="password" id="swal-new-password" class="swal2-input" placeholder="New Password" />
                    <input type="password" id="swal-confirm-password" class="swal2-input" placeholder="Confirm Password" />
                `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Reset Password',
                confirmButtonColor: '#57d6d4ff',
                preConfirm: () => {
                    const newPassword = document.getElementById('swal-new-password')?.value || '';
                    const confirmPassword = document.getElementById('swal-confirm-password')?.value || '';

                    if (!newPassword.trim() || !confirmPassword.trim()) {
                        Swal.showValidationMessage('Please enter password and confirmation');
                        return false;
                    }

                    if (newPassword !== confirmPassword) {
                        Swal.showValidationMessage('Password and confirmation do not match');
                        return false;
                    }

                    return { newPassword, confirmPassword };
                },
            });

            if (!resetValues) return;

            await postAuthForm('ws-reset-password', {
                username: normalizedUsername,
                otp: normalizedOtp,
                password: resetValues.newPassword,
                password_confirmation: resetValues.confirmPassword,
            });

            setUsername(normalizedUsername);

            Swal.fire({
                icon: 'success',
                title: 'Password Reset Successful',
                text: 'You can now login with your new password.',
                confirmButtonColor: '#57d6d4ff',
            });
        } catch (forgotError) {
            Swal.fire({
                icon: 'error',
                title: 'Forgot Password Failed',
                text: forgotError?.message || 'Something went wrong. Please try again.',
                confirmButtonColor: '#d33'
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!username || !password) {
            setError('Please enter both username and password');
            return;
        }

        try {
            const buildPlainAuthFormData = (fields) => {
                const fd = new FormData();
                Object.entries(fields).forEach(([key, value]) => {
                    fd.append(key, value ?? '');
                });
                return fd;
            };
            const buildMaybeEncryptedFormData = (fields) => {
                const fd = new FormData();
                const encrypted = encryptPayload(fields);
                if (encrypted) {
                    fd.append('encrypted_payload', encrypted);
                } else {
                    Object.entries(fields).forEach(([key, value]) => {
                        fd.append(key, value ?? '');
                    });
                }
                return fd;
            };
            let logoutOtherDeviceConfirmed = false;
            const checkFormData = buildPlainAuthFormData({
                username,
                platform: 'Web',
            });
            const checkResponse = await fetch(`${API_URL}/ws-check-if-already-logged-in`, {
                method: 'POST',
                body: checkFormData,
            });
            const rawCheckData = await checkResponse.json();
            const checkData = resolveEncryptedResponse(rawCheckData) || rawCheckData;

            if (checkData && checkData.success === true && checkData.message) {
                const result = await Swal.fire({
                            html: "You've already logged in on another device.<br/>Do you want to log out from the previous device and continue here?",
                            showCancelButton: true,
                            confirmButtonText: 'Logout and Continue',
                            cancelButtonText: 'Cancel',
                            confirmButtonColor: '#57d6d4ff',
                            cancelButtonColor: '#d33',
                            customClass: {
                                confirmButton: 'swal2-sm-button',
                                cancelButton: 'swal2-sm-button'
                            }
                        });

                if (!result.isConfirmed) {
                    return;
                }
                logoutOtherDeviceConfirmed = true;
            }

            if (logoutOtherDeviceConfirmed) {
                const last =
                    (checkData && checkData.lastLoginData) ||
                    (checkData && checkData.data && checkData.data.lastLoginData);
                const prevJwt =
                    (last && last.jwtTokenWeb) ||
                    (last && last.jwtToken) ||
                    (checkData && checkData.jwtTokenWeb) ||
                    (checkData && checkData.token);
                if (prevJwt) {
                    const logoutFd = buildMaybeEncryptedFormData({
                        Platform: 'Web',
                    });
                    try {
                        await fetch(`${API_URL}/ws-logout`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${prevJwt}` },
                            body: logoutFd,
                        });
                    } catch (_) {}
                    try {
                        const logoutFd2 = buildMaybeEncryptedFormData({
                            Platform: 'Web',
                            token: prevJwt,
                        });
                        await fetch(`${API_URL}/ws-logout`, {
                            method: 'POST',
                            body: logoutFd2,
                        });
                    } catch (_) {}
                }
            }

            const loginPayload = {
                username,
                password,
                deviceName: 'Web',
            };
            if (logoutOtherDeviceConfirmed) {
                loginPayload.logoutOtherSessions = '1';
            }
            const formData = buildPlainAuthFormData(loginPayload);
            const response = await fetch(`${API_URL}/ws-login-v2`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const message = await getResponseMessage(response, 'Login failed');
                throw new Error(message);
            }

            const rawData = await response.json();
            const data = resolveEncryptedResponse(rawData) || rawData;
            // console.log('Login successful:', data);
            localStorage.setItem('accessToken', data.prefix);
            localStorage.setItem('token', data.jwtTokenWeb);
            localStorage.setItem('username', username);
            localStorage.setItem('user_role', data.role);
            localStorage.setItem('userId', data.profile_details.id);
            localStorage.setItem('site_name', data.site_name);
            localStorage.setItem('first_name', data.profile_details.first_name);
            localStorage.setItem('last_name', data.profile_details.last_name);
            localStorage.setItem('company_tax_type', data.company_tax_type);
            localStorage.setItem('currencySymbol', data.currencySymbol);
            localStorage.setItem('isShowRutePlan', data.isShowRutePlan);

            clearUserRolesCache();
            await prefetchCompanySettings();
            await prefetchUserRolesForPermission();
            const prefetchedPerms = await prefetchUserRolesWithPermissions();
            
            const normalizePermissionsMapKeys = (map) => {
                if (!map || typeof map !== "object" || Array.isArray(map)) return {};
                const normalized = {};
                Object.entries(map).forEach(([roleId, permissions]) => {
                    const id = String(roleId ?? "").trim();
                    if (!id || !Array.isArray(permissions)) return;
                    normalized[id] = permissions;
                });
                return normalized;
            };

            const loginPermissionsRaw = data.permissions;
            let loginPermissionsList = [];
            let loginPermissionsMap = {};
            if (Array.isArray(loginPermissionsRaw)) {
                loginPermissionsList = loginPermissionsRaw;
            } else if (loginPermissionsRaw && typeof loginPermissionsRaw === "object") {
                loginPermissionsMap = normalizePermissionsMapKeys(loginPermissionsRaw);
            }

            const prefetchedRoles = Array.isArray(prefetchedPerms?.data?.data?.roles)
                ? prefetchedPerms.data.data.roles
                : [];
            const prefetchedPermissionsMap = normalizePermissionsMapKeys(
                prefetchedPerms?.data?.data?.permissions
            );

            if (data.accessible_role && Array.isArray(data.accessible_role) && data.accessible_role.length > 0) {
                const roles = data.accessible_role.map(role => ({
                    id: role.role_id.toString(),
                    name: role.name || `Role ${role.role_id}`
                }));
                localStorage.setItem('user_roles_data', JSON.stringify(roles));

                const userIdStr = String(data.profile_details.id);
                const lastRoleKey = `last_active_role_${userIdStr}`;
                const lastRole = localStorage.getItem(lastRoleKey);
                const preferredRoleId =
                    lastRole && roles.some((r) => String(r.id) === String(lastRole))
                        ? String(lastRole)
                        : String(data.role || roles[0].id);

                localStorage.setItem('active_role_id', preferredRoleId);
                localStorage.setItem('user_role', preferredRoleId);
                localStorage.setItem(lastRoleKey, preferredRoleId);
                
                let permissionsMap = normalizePermissionsMapKeys({
                    ...loginPermissionsMap,
                    ...(loginPermissionsList.length > 0
                        ? {
                            [String(data.role)]: loginPermissionsList,
                            [preferredRoleId]: loginPermissionsList,
                          }
                        : {}),
                    ...prefetchedPermissionsMap,
                });

                try {
                    const bearer = data.jwtTokenWeb || localStorage.getItem('token');
                    const { response: rolePermRes, data: rolePermData } =
                        (await fetchUserRolesWithPermissionsCached(bearer, {
                            userId: userIdStr,
                            roleId: preferredRoleId,
                            force: true,
                        })) || {};
                    if (
                        rolePermRes?.ok &&
                        rolePermData?.success &&
                        rolePermData?.data?.permissions
                    ) {
                        permissionsMap = normalizePermissionsMapKeys({
                            ...permissionsMap,
                            ...rolePermData.data.permissions,
                        });
                    } else if (Object.keys(prefetchedPermissionsMap).length === 0 && roles.length > 1) {
                        const { response, data: permData } =
                            (await fetchUserRolesWithPermissionsCached(bearer, {
                                userId: userIdStr,
                                force: true,
                            })) || {};
                        if (response?.ok && permData?.success && permData?.data?.permissions) {
                            permissionsMap = normalizePermissionsMapKeys({
                                ...permissionsMap,
                                ...permData.data.permissions,
                            });
                        }
                    }
                } catch {
                }

                localStorage.setItem('role_permissions_map', JSON.stringify(permissionsMap));
                const preferredPerms = Array.isArray(permissionsMap[preferredRoleId])
                    ? permissionsMap[preferredRoleId]
                    : loginPermissionsList;
                localStorage.setItem('user_permissions', JSON.stringify(preferredPerms || []));
            } else if (data.role) {
                const roleIdStr = String(data.role);
                const roleFromPrefetch = prefetchedRoles.find(
                    (r) => String(r.id ?? r.role_id) === roleIdStr
                );
                const singleRole = {
                    id: roleIdStr,
                    name: roleFromPrefetch?.name || `Role ${data.role}`
                };
                localStorage.setItem('user_roles_data', JSON.stringify([singleRole]));
                localStorage.setItem('active_role_id', roleIdStr);
                localStorage.setItem('user_role', roleIdStr);
                localStorage.setItem(`last_active_role_${data.profile_details.id}`, roleIdStr);
                
                let permissionsMap = normalizePermissionsMapKeys({
                    ...loginPermissionsMap,
                    ...(loginPermissionsList.length > 0
                        ? { [roleIdStr]: loginPermissionsList }
                        : {}),
                    ...prefetchedPermissionsMap,
                });

                try {
                    const bearer = data.jwtTokenWeb || localStorage.getItem('token');
                    const { response: rolePermRes, data: rolePermData } =
                        (await fetchUserRolesWithPermissionsCached(bearer, {
                            userId: String(data.profile_details.id),
                            roleId: roleIdStr,
                            force: true,
                        })) || {};
                    if (
                        rolePermRes?.ok &&
                        rolePermData?.success &&
                        rolePermData?.data?.permissions
                    ) {
                        permissionsMap = normalizePermissionsMapKeys({
                            ...permissionsMap,
                            ...rolePermData.data.permissions,
                        });
                    }
                } catch {
                }

                localStorage.setItem('role_permissions_map', JSON.stringify(permissionsMap));
                const rolePerms = Array.isArray(permissionsMap[roleIdStr])
                    ? permissionsMap[roleIdStr]
                    : loginPermissionsList;
                localStorage.setItem('user_permissions', JSON.stringify(rolePerms || []));
            } else {
                localStorage.setItem('user_permissions', JSON.stringify(loginPermissionsList));
            }

            markRolesSessionHydrated(String(data.profile_details.id));

            window.dispatchEvent(new CustomEvent('userLoggedIn', {
                detail: {
                    role: localStorage.getItem('active_role_id') || data.role,
                    permissions: JSON.parse(localStorage.getItem('user_permissions') || '[]'),
                },
            }));


            let url = data.url || '';
            if (BASE_URL != 'http://localhost:3000') {
                if (BASE_URL.trim().replace(/\/$/, '') !== url.trim().replace(/\/$/, '')) {
                    setError('Login failed');
                    return; // Stop further execution
                }
            }
          //  navigate('/dashboard/');
            const activeLoginRole = String(
              localStorage.getItem('active_role_id') || data.role || ''
            );
            if (activeLoginRole === '18') {
                fetchTillOptions(data.prefix);
                setOpenModal(true);
                setError(null);
            } else if (activeLoginRole == 19) {
                navigate(`sales/IncompleteOrderList`);
                setError(null);
            } else if (activeLoginRole == 20) {
                navigate(`/sales/quick-order`);
                setError(null);
            } else {
                // Admin (1), Manager (8), Accountant (22), and other roles
                navigate(`/dashboard/${data.prefix}/${data.profile_details.id}`);
                setError(null);
            }

        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Login Failed',
                text: 'Incorrect username or password',
                confirmButtonColor: '#d33'
            });
        }
    };

    async function fetchTillOptions(token) {
        try {
            const response = await axios.post(
                `${API_URL}/till_number`,
                {},
                {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.data || !response.data.data || !Array.isArray(response.data.data)) {
                throw new Error('Invalid response format or empty vehicle array');
            }

            const formattedOptions = response.data.data.map(item => ({
                key: item.number,
                value: item.id,
            }));

            setOptions(formattedOptions);
        } catch (error) {
            console.error('Error fetching vehicle options:', error);
        }
    }

    return (
        <div className="login-body d-flex flex-column h-100">
            <div className="login-container">
                <div className="login-content">
                    {openModal && (
                        <div
                            ref={modalRef}
                            className={`modal fade ${openModal ? "show d-block" : ""}`}
                            id="vehicleMdl verticalycentered"
                            tabIndex="-1"
                            role="dialog"
                            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                        >
                            <div className="modal-dialog modal-dialog-centered modal-sm login-till-modal-dialog" role="document">
                                <div className="modal-content loginpopup-bg">
                                    <div className="modal-header lg-popup-bg">
                                        <h5 className="modal-title">Select Till Number </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setOpenModal(false)}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                    <div className="modal-body lg-popup-body">
                                        <select
                                            value={selectedTill}
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                const selectedOption = options.find((option) => option.value === selectedValue);
                                                setSelectedTill(selectedValue);
                                                setSelectedTillName(selectedOption ? selectedOption.key : '');
                                            }}
                                            className="datatable-selector form-select lg-select"
                                            name="driver_list"
                                            id="driver_list"
                                            style={{ marginRight: "15px" }}
                                        >
                                            <option value="0">Select </option>
                                            {options.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.key}
                                                </option>
                                            ))}
                                        </select>

                                    </div>
                                    <div className="modal-footer lg-popup-footer">
                                        <button
                                            type="button"
                                            className="lg-close-btn"
                                            onClick={() => setOpenModal(false)}
                                        >
                                            Close
                                        </button>
                                        <button
                                            type="button"
                                            className="float-right-btn lg-sbmit-btn"
                                            onClick={() => {
                                                if (selectedTill === '0') {
                                                    Swal.fire({
                                                        title: 'Error',
                                                        text: 'Please Select a till to login.',
                                                        icon: 'error',
                                                        showConfirmButton: false,
                                                        timer: 1500,
                                                        timerProgressBar: true,
                                                    });
                                                }
                                                else {
                                                    navigate(`sales/epos/new`);
                                                    localStorage.setItem('tillUserId', selectedTill);
                                                    localStorage.setItem('tillUserName', selectedTillName);
                                                }
                                            }}>
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="login-content_header">
                        <span className="logo">
                            <img src={`assets/img/${PREFIX}_logo.png`} alt="Ebusso Logo" />
                        </span>
                        <p>Please login to your account.</p>
                    </div>
                    <div className="login-form-section">
                        <form className="login-form" onSubmit={handleSubmit}>
                            <input type="text" placeholder="Username" value={username} onChange={handleUsernameChange} autoComplete="username" />
                            <input type="password" placeholder="Password" value={password} onChange={handlePasswordChange} autoComplete="current-password" />
                            {error && <p className="text-danger">{error}</p>}
                            <button type="submit" className="login-submit-btn">Login</button>
                        </form>
                        <div className="login-networks">
                            <p>
                                <button
                                    type="button"
                                    className="login-forgot-link"
                                    onClick={handleForgotPassword}
                                >
                                    Forgot password?
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default Login;
